import React, {useEffect, useState} from "react";
import {useDataWithRoute, useTitleFromComponent} from "../../../utils/hooks";
import {useNavigate, useSearchParams} from "react-router-dom";
import {VsiBubble, VsiH4, VsiHr, VsiLgText, VsiMedText, VsiSmallText} from "../../../styled/App.styled";
import VsiLabeledSelect from "../../../components/VsiLabeledSelect";
import {Row, Col, UncontrolledTooltip, Card} from "reactstrap";

import {useVsiAppContext} from "../../../stores/VsiApp";
import {iopsLineColor, latencyLineColor, determineEnvBarColor, determineEnvBackground} from "../../../utils/chartUtils";
import Grid from "../../../components/Grid";
import {
    currentFormatter,
    fractionPercentageBarFormatter,
    nAForNullFormatter,
    numberFormatter,
} from "../../../utils/gridFormatters";
import {EnvCard, EnvHeader} from "../../../styled/Environment";
import themeStyles from "../../../utils/themeStyles";
import VsiModal from "../../../components/VsiModal";
import EnvironmentProgressBar from "../../../components/EnvironmentProgressBar";
import { wtPctAvg } from "../../../utils/math";

const chartGroupings = [
    {value: 'data_center', label: 'Data Center', iconText: 'fa-warehouse'},
    {value: 'classification', label: 'Classification', iconText: 'fa-database'},
    {value: 'geography', label: 'Geography', iconText: 'fa-location-dot'},
];


const devicesStyle = {
    display: 'grid',
    gridTemplateColumns: 'repeat(2, 1fr)',
    gridGap: '1rem',
    gridTemplateRows: 'masonry',
}

const gridDefs = [
    {
        "field": "device_name",
        "headerName": "Device Name",
    },
    {
        "field": "data_center",
        "headerName": "Data Center",
        "enableValue": true,
        "cellRenderer": nAForNullFormatter,
    },
    {
        "field": "device_type_description",
        "headerName": "Type",
        "filterParams": {
            "cellHeight": 20,
            "debounceMs": 1000
        },
        "enableRowGroup": true,
        "cellRenderer": nAForNullFormatter,
    },
    {
        "field": "classification",
        "headerName": "Classification",
        "enableRowGroup": true,
        "cellRenderer": nAForNullFormatter,
    },
    {
        "field": "geography",
        "headerName": "Geography",
        "enableRowGroup": true,
        "cellRenderer": nAForNullFormatter,
    },
    {
        "field": "ttl_allocated",
        "headerName": "Allocated (GiB)",
        "type": "numericColumn",
        "enableValue": true,
        "aggFunc": "sum",
        "cellStyle": {
            "textAlign": "right"
        },
        "filter": "agNumberColumnFilter",
        "filterParams": {
            "cellHeight": 20,
            "debounceMs": 1000
        },
        "cellRenderer": numberFormatter
    },
    {
        "field": "ttl_used",
        "headerName": "Used (GiB)",
        "type": "numericColumn",
        "enableValue": true,
        "aggFunc": "sum",
        "cellStyle": {
            "textAlign": "right"
        },
        "filter": "agNumberColumnFilter",
        "filterParams": {
            "cellHeight": 20,
            "debounceMs": 1000
        },
        "cellRenderer": numberFormatter
    },
    {
        "field": "ttl_usable",
        "headerName": "Usable (GiB)",
        "type": "numericColumn",
        "enableValue": true,
        "aggFunc": "sum",
        "cellStyle": {
            "textAlign": "right"
        },
        "filter": "agNumberColumnFilter",
        "filterParams": {
            "cellHeight": 20,
            "debounceMs": 1000
        },
        "cellRenderer": numberFormatter
    },
    {
        "field": "pct_used",
        "headerName": "% Used",
        // "enableValue": true,
        "aggFunc": params => wtPctAvg(params, "ttl_used", "ttl_usable") / 100,
        "filter": "agNumberColumnFilter",
        "filterParams": {
            "cellHeight": 20,
            "debounceMs": 1000
        },
        "cellRenderer": fractionPercentageBarFormatter
    },
    {
        "field": "ttl_iops",
        "headerName": "IOPS",
        "type": "numericColumn",
        "enableValue": true,
        "aggFunc": "sum",
        "cellStyle": {
            "textAlign": "right"
        },
        "filter": "agNumberColumnFilter",
        "filterParams": {
            "cellHeight": 20,
            "debounceMs": 1000
        },
        "cellRenderer": numberFormatter
    },
    {
        "field": "ttl_latency",
        "headerName": "weighted/Latency",
        "type": "numericColumn",
        "enableValue": true,
        "cellStyle": {
            "textAlign": "right"
        },
        "aggFunc": params => {
            let subTotalIOPs = 0;
            let weightedIOPsLat = 0;
            (params.rowNode.childrenAfterAggFilter || params.rowNode.childrenAfterFilter || params.rowNode.allLeafChildren).forEach(rN => {
                    subTotalIOPs += rN.data.ttl_iops;
                    weightedIOPsLat += rN.data.ttl_iops * rN.data.ttl_latency;
                }
            )
            if (subTotalIOPs === 0) {
                subTotalIOPs = 1 // prevent zero divider
            }
            return weightedIOPsLat / subTotalIOPs;
        },
        "filter": "agNumberColumnFilter",
        "filterParams": {
            "cellHeight": 20,
            "debounceMs": 1000
        },
        "cellRenderer": numberFormatter
    },
    {
        "field": "current",
        "headerName": "Days Old",
        "filter": "agNumberColumnFilter",
        "hide": true,
        "cellRenderer": currentFormatter,
        "cellStyle": {
            "textAlign": "center"
        }
    },
];

const accumFields = {
    ttl_usable: 0,
    ttl_used: 0,
    ttl_iops: 0,
    ttl_latency: 0,
    weightedLatency: 0
}

const StorageEnvironmentCard = ({keyData, chartGrouping, name}) => {
    const {theme} = useVsiAppContext();

    const {ttl_used, ttl_usable, ttl_iops, ttl_latency, weightedLatency} = keyData;
    const pct_used = (ttl_used / ttl_usable) * 100;
    const allDeviceTypes = Object.keys(keyData.all_device_types);

    return (
        <EnvCard>
            <EnvHeader backgroundColor={determineEnvBackground(pct_used)}>
                <div className={"d-flex flex-row align-items-center justify-content-between"}>
                    <div>
                        <i className={`fa-solid ${chartGrouping.iconText} me-2`}
                           style={{color: themeStyles[theme].textColor}}/>
                        <VsiLgText>
                            {name}
                        </VsiLgText>
                    </div>
                    <div>
                        <VsiLgText>{(Math.round(ttl_usable / 1024 * 10) / 10)?.toLocaleString?.() ?? 0} TiB</VsiLgText>
                    </div>
                </div>
                <EnvironmentProgressBar value={pct_used}/>
            </EnvHeader>
            <div className={"p-2"} style={devicesStyle}>
                {allDeviceTypes.map((typeKey, typeIndex) => {
                    const deviceInfo = keyData.all_device_types[[typeKey]];

                    return <StorageEnvironmentDeviceCard key={`device-${typeKey}-${typeIndex}`}
                                                         group={name}
                                                         deviceName={typeKey}
                                                         deviceInfo={deviceInfo}/>
                })}
            </div>
            <div className={"p-2 d-flex flex-row align-items-center justify-content-around"}>
                {ttl_iops ? <span>
                                  <i className="pe-1 fa-solid fa-gauge"
                                     id={`${name.replace(/[.\s\\\/()]/g, '')}-iops-tip`}
                                     style={{color: iopsLineColor}}/>
                                  <UncontrolledTooltip target={`${name.replace(/[.\s\\\/()]/g, '')}-iops-tip`}>
                                      IOPs
                                  </UncontrolledTooltip>
                                  <VsiLgText>
                                      {Math.round(ttl_iops)}
                                  </VsiLgText>
                              </span> : null}
                {ttl_latency ? <span>
                                      <i className="pe-1 fa-solid fa-stopwatch"
                                         id={`${name.replace(/[.\s\\\/()]/g, '')}-latency-tip`}
                                         style={{color: latencyLineColor}}/>
                                <UncontrolledTooltip target={`${name.replace(/[.\s\\\/()]/g, '')}-latency-tip`}>
                                      Latency
                                  </UncontrolledTooltip>
                                  <VsiLgText>
                                      {Math.round(weightedLatency * 1000) / 1000}
                                  </VsiLgText>
                              </span> : null}
            </div>
        </EnvCard>
    )
}

const StorageEnvironmentDeviceCard = ({deviceInfo, deviceName, group}) => {

    const {theme} = useVsiAppContext();
    let navigate = useNavigate();

    const [isModalOpen, setIsModalOpen] = useState(false);
    const toggleModal = () => setIsModalOpen(!isModalOpen);

    const devices = deviceInfo.devices;
    const pct_used = (deviceInfo.ttl_used / deviceInfo.ttl_usable) * 100;

    const bgColor = determineEnvBackground(pct_used);
    const barColor = determineEnvBarColor(pct_used);

    const onDeviceClick = data => {
        navigate(`/reports/storage/device/device-summary?device_name=${encodeURI(data.device_name)}&wwn=${data.wwn}&period_id=${data.period_id}`)
    };

    return (
        <div className="p-2 d-flex flex-column"
             onClick={toggleModal}
             style={{
                 border: `1px solid ${barColor}`,
                 borderRadius: '5px',
                 backgroundColor: bgColor,
                 cursor: 'pointer',
             }}>
            <div className={"mb-3"}>
                <i className="fa-solid fa-hard-drive me-2" style={{color: themeStyles[theme].textColor}}/>
                <VsiMedText textAlign={'left'}>
                    {deviceName} ({devices.length})
                </VsiMedText>
            </div>
            <VsiMedText
                textAlign={"left"}>{(Math.round(deviceInfo.ttl_used / 1024 * 10) / 10)?.toLocaleString?.() ?? 0} / {(Math.round(deviceInfo.ttl_usable / 1024 * 10) / 10)?.toLocaleString?.() ?? 0} TiB</VsiMedText>

            <EnvironmentProgressBar value={Math.round(pct_used)}/>
            <VsiModal isOpen={isModalOpen}
                      header={<div>
                          <VsiH4>{devices.length} {deviceName}(s) in {group}</VsiH4>
                          <VsiSmallText>(double click a device name for more details)</VsiSmallText>
                      </div>}
                      toggle={toggleModal}>
                {devices.map((device, deviceIndex) => {
                    const pctUsed = device.pct_used * 100;

                    return (
                        <div className={"mb-1"} key={`device-modal-${deviceName}-${deviceIndex}`}>
                            <Row>
                                <Col xs={9}>
                                    <VsiLgText onDoubleClick={() => onDeviceClick(device)} style={{cursor: 'pointer'}}>
                                        {device.device_name} ({deviceIndex + 1})
                                    </VsiLgText>
                                    <EnvironmentProgressBar value={Math.round(pctUsed)}/>
                                </Col>
                                <Col xs={3} className={"d-flex flex-column align-items-center justify-content-center"}>
                                    <div>
                                        <i className="pe-1 fa-solid fa-gauge" style={{color: iopsLineColor}}/>
                                        <VsiLgText>{device.ttl_iops?.toLocaleString?.() ?? 0}</VsiLgText>
                                    </div>
                                    <div>
                                        <i className="pe-1 fa-solid fa-stopwatch" style={{color: latencyLineColor}}/>
                                        <VsiLgText>{device?.ttl_latency?.toLocaleString?.() ?? 0}</VsiLgText>
                                    </div>
                                </Col>
                            </Row>
                            <VsiHr/>
                        </div>
                    )
                })}
            </VsiModal>
        </div>
    )
}

export default function StorageEnvironment({title, context}) {
    useTitleFromComponent(`${context} / ${title}`);
    const { theme, isTabletScreen, client, periodId } = useVsiAppContext();

    const [searchParams, setSearchParams] = useSearchParams();

    const grouping = searchParams.get('grouping') || chartGroupings[0].value;
    let defaultGrouping = chartGroupings.find(({value}) => grouping === value) || chartGroupings[0];
    const [chartGrouping, setChartGrouping] = useState(defaultGrouping);

    const [visibleData, setVisibleData] = useState([]);
    const [envData, setEnvData] = useState([]);

    const [{recordset: data}, {isLoading,}] = useDataWithRoute(`/api/v6/report/enterprise_storage_environment_proc?client_id=${client.clientId}&period_id=${periodId}`);

    const onGroupChange = (type) => {
        setChartGrouping(type);
        searchParams.set('grouping', type.value);
        setSearchParams(searchParams, {replace: true});
    };

    const createEnvironmentGrouping = (data) => {
        /*
        Weighted latency is done with:
            1. In each group, if weightedLatency hasn't been defaulted, default it to the first one it encounters.
            2. When looping, everytime meets a weightedLatency, do one weighted cals with current number
        */

        const grouping = {};
        data.forEach(datum => {
            const groupValue = datum?.[chartGrouping.value] || 'N/A';
            // If Data Center doesn't exist yet
            if (!grouping[groupValue]) {
                grouping[groupValue] = {
                    ...datum,
                    devicesCount: 1,
                    all_device_types: {
                        [datum["device_type_description"].trim()]: {
                            ...datum,
                            devices: [datum]
                        }
                    },
                    weightedLatency: datum.ttl_latency  // default as the first latency it encounters
                };
            } else {
                // If device type doesn't exist yet
                if (!grouping[groupValue].all_device_types[datum["device_type_description"].trim()]) {
                    // before touching the group, do weighted cals
                    let wLat = 0;
                    let sumIOPs = grouping[groupValue].ttl_iops + datum.ttl_latency;
                    if (sumIOPs === 0) {
                        sumIOPs = 1 // prevent zero divider
                    }
                    wLat = (grouping[groupValue].weightedLatency * grouping[groupValue].ttl_iops
                        + datum.ttl_latency * datum.ttl_latency) / sumIOPs
                    grouping[groupValue].weightedLatency = wLat

                    // Adds up total per Data Center
                    Object.keys(accumFields).forEach(key => {
                        // skip weightedLatency
                        if (key === 'weightedLatency') {
                            return;
                        }
                        grouping[groupValue][key] = grouping[groupValue][key] + datum[key];
                    });

                    // Adds first device to all_device_types
                    grouping[groupValue].all_device_types[datum["device_type_description"].trim()] = {
                        ...datum,
                        devices: [datum]
                    }
                } else {
                    // before touching the group, do weighted cals
                    let wLat = 0;
                    let sumIOPs = grouping[groupValue].ttl_iops + datum.ttl_iops;
                    if (sumIOPs === 0) {
                        sumIOPs = 1 // prevent zero divider
                    }
                    wLat = (grouping[groupValue].weightedLatency * grouping[groupValue].ttl_iops
                        + datum.ttl_latency * datum.ttl_iops) / sumIOPs
                    grouping[groupValue].weightedLatency = wLat

                    Object.keys(datum).forEach(key => {
                        if (typeof datum[key] === "number" && !key.includes("_id")) {
                            // Adds to total per Data Center
                            grouping[groupValue][key] = grouping[groupValue][key] + datum[key];

                            // Adds up for each device
                            const numInPlace = grouping[groupValue].all_device_types[datum["device_type_description"].trim()][key] || 0;
                            grouping[groupValue].all_device_types[datum["device_type_description"].trim()][key] = numInPlace + datum[key];
                        }
                    });

                    grouping[groupValue].all_device_types[datum["device_type_description"].trim()].devices.push(datum);
                }
                grouping[groupValue].devicesCount = grouping[groupValue].devicesCount + 1;
            }
        });

        // ===========RECALCULATING WEIGHTED LATENCY=======================
        Object.keys(grouping).forEach(k => {
            let key = k === 'N/A' ? '' : k;
            let totalNum = 0.00;
            let totalDen = 0.00;
            data.forEach(d => {
                if (d[chartGrouping.value] === key) {
                    totalNum += (d.ttl_iops * d.ttl_latency);
                    totalDen += d.ttl_iops;
                }
            });
            grouping[k].weightedLatency = totalDen && totalDen > 0 ? totalNum / totalDen : 0.00;
        });
        return grouping;

    }

    useEffect(() => {
        setVisibleData(data);
    }, [data]);

    useEffect(() => {
        const grouping = createEnvironmentGrouping(visibleData);
        setEnvData(grouping);
    }, [visibleData, chartGrouping]);

    const filterChanged = (eventData) => {
        let rowData = [];
        eventData.api.forEachNodeAfterFilter(node => {
            rowData.push(node.data);
        });
        setVisibleData(rowData || []);
    }

    const envKeys = Object.keys(envData);

    const allTypesLengths = envKeys.map(key => ({
        dataCenter: key,
        deviceTypeCount: Object.keys(envData[key].all_device_types).length
    })).sort((a, b) => b.deviceTypeCount - a.deviceTypeCount);

    const sides = function () {
        const left = {
            centers: {},
            count: 0,
        };
        const right = {
            centers: {},
            count: 0,
        };

        allTypesLengths.forEach((center) => {
            if (left.count > (right.count + center.deviceTypeCount)) {
                right.count = right.count + center.deviceTypeCount;
                right.centers[center.dataCenter] = envData[center.dataCenter];
            } else {
                left.count = left.count + center.deviceTypeCount;
                left.centers[center.dataCenter] = envData[center.dataCenter];
            }
        });

        return {
            left,
            right,
        }
    }();

    const leftSide = Object.keys(sides.left.centers);
    const rightSide = Object.keys(sides.right.centers);

    return (
        <Card className={`report-card p-2 nav-card-${theme}`}>
            <VsiH4>{`Storage Environment by ${chartGrouping.label}`}</VsiH4>
            <VsiBubble noAutobot>
                <VsiLabeledSelect options={chartGroupings}
                                  isDisabled={isLoading}
                                  label={'Grouping'}
                                  minWidth={150}
                                  onChange={onGroupChange}
                                  defaultValue={chartGrouping}/>
            </VsiBubble>
            <VsiBubble autobotName='StorageEnvironmentCards' unit={chartGrouping.value} autobotInfo={{data: envData}}>
                {isTabletScreen ? (
                    <div className={"w-100 px-2"}>
                        {envKeys.map((key, index) => {
                            const keyData = envData[key];
                            return (
                                <StorageEnvironmentCard key={`left-column-${key}-${index}`}
                                                        name={key}
                                                        chartGrouping={chartGrouping}
                                                        keyData={keyData}/>
                            )
                        })}
                    </div>
                ) : (
                    <Row>
                        <div className={"w-50 px-2"}>
                            {leftSide.map((key, index) => {
                                const keyData = envData[key];
                                return (
                                    <StorageEnvironmentCard key={`left-column-${key}-${index}`}
                                                            name={key}
                                                            chartGrouping={chartGrouping}
                                                            keyData={keyData}/>
                                )
                            })}
                        </div>
                        <div className={"w-50 px-2"}>
                            {rightSide.map((key, index) => {
                                const keyData = envData[key];
                                return (
                                    <StorageEnvironmentCard key={`right-column-${key}-${index}`}
                                                            name={key}
                                                            chartGrouping={chartGrouping}
                                                            keyData={keyData}/>
                                )
                            })}
                        </div>
                    </Row>
                )}
            </VsiBubble>
            <VsiBubble unit={chartGrouping.value}>
                <Grid data={data} columnDefs={gridDefs}
                      filterChanged={filterChanged}/>
            </VsiBubble>
        </Card>
    )
}
